import { useEffect, useRef, useState } from 'react';
import { Briefcase, GraduationCap, MapPin, Sparkles } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@ah/design';
import { ResizedImage } from '@ah/design/mobile';
import type {
  NominationSuggestion,
  NominationSuggestionExperience,
  NominationSuggestionEducation,
} from '@ah/people/shared';
import { Section } from './_shared/Section';
import { countryFlag, formatYearRange, initialsOf } from './_shared/formatters';

function ExpandableAbout({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  const canExpand = clamped || expanded;

  return (
    <button
      type="button"
      onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
      className="block w-full text-left"
      aria-expanded={expanded}
    >
      <p
        ref={ref}
        className={`text-[15px] text-foreground/80 leading-relaxed ${expanded ? '' : 'line-clamp-4'}`}
      >
        {text}
      </p>
      {canExpand && (
        <span className="mt-1 inline-block text-[13px] font-medium text-primary">
          {expanded ? 'Show less' : 'Show more'}
        </span>
      )}
    </button>
  );
}

function MatchReasons({ reasons }: { reasons: string[] }) {
  return (
    <ul className="space-y-2.5">
      {reasons.map((reason, i) => (
        <li key={i} className="flex items-start gap-2.5 text-[15px]">
          <Sparkles size={14} className="text-primary shrink-0 mt-[3px]" />
          <span className="leading-snug">{reason}</span>
        </li>
      ))}
    </ul>
  );
}

function RowIcon({ url, fallback }: { url: string | null; fallback: React.ReactNode }) {
  return (
    <div className="size-6 shrink-0 mt-0.5 flex items-center justify-center">
      {url ? (
        <Avatar className="size-6">
          <AvatarImage src={url} alt="" />
          <AvatarFallback className="text-[11px]">{fallback}</AvatarFallback>
        </Avatar>
      ) : (
        fallback
      )}
    </div>
  );
}

function ExperienceRow({ exp }: { exp: NominationSuggestionExperience }) {
  const range = exp.duration ?? formatYearRange(exp.startDate, exp.endDate);
  return (
    <div className="flex gap-2.5 text-[15px]">
      <RowIcon
        url={exp.organizationPictureUrl}
        fallback={<Briefcase size={14} className="text-muted-foreground" />}
      />
      <div className="min-w-0">
        <p className="font-medium leading-tight">{exp.title ?? 'Role'}</p>
        <p className="text-[13px] text-muted-foreground leading-tight mt-1">
          {exp.company}{range ? ` · ${range}` : null}
        </p>
      </div>
    </div>
  );
}

function EducationRow({ ed }: { ed: NominationSuggestionEducation }) {
  const detail = [ed.degreeName ?? ed.degree, ed.fieldOfStudy].filter(Boolean).join(' · ');
  const range = formatYearRange(ed.startDate, ed.endDate);
  return (
    <div className="flex gap-2.5 text-[15px]">
      <RowIcon
        url={ed.organizationPictureUrl}
        fallback={<GraduationCap size={14} className="text-muted-foreground" />}
      />
      <div className="min-w-0">
        <p className="font-medium leading-tight">{ed.school ?? 'Unknown school'}</p>
        {(detail || range) && (
          <p className="text-[13px] text-muted-foreground leading-tight mt-1">
            {detail}{detail && range ? ' · ' : ''}{range ?? ''}
          </p>
        )}
      </div>
    </div>
  );
}

export function PersonCard({ person }: { person: NominationSuggestion }) {
  const current = person.experiences.find((e) => e.isCurrent) ?? person.experiences[0] ?? null;
  const past = person.experiences.filter((e) => e !== current).slice(0, 3);
  const education = person.education.slice(0, 2);
  const almaMater = person.education.find((e) => e.school) ?? null;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
      {person.pictureUrl ? (
        <div className="w-full aspect-square bg-muted overflow-hidden">
          <ResizedImage src={person.pictureUrl} renderWidth={384} fallback={false} alt={person.name ?? ''} className="w-full h-full object-cover object-top" />
        </div>
      ) : (
        <div className="w-full aspect-square bg-muted flex items-center justify-center">
          <span className="text-6xl font-bold text-muted-foreground/30">{initialsOf(person.name)}</span>
        </div>
      )}

      <div className="p-6 space-y-5">
        <div className="min-w-0">
          {person.name && <p className="font-bold text-xl leading-tight">{person.name}</p>}
          {person.headline && (
            <p className="text-[15px] text-muted-foreground mt-1.5 leading-snug">{person.headline}</p>
          )}
          {person.locationRaw && (
            <p className="flex items-center gap-1 text-[13px] text-muted-foreground mt-2">
              {person.locationCountryCode
                ? <span>{countryFlag(person.locationCountryCode)}</span>
                : <MapPin size={13} />}
              {person.locationRaw}
            </p>
          )}
          {(almaMater?.school || current?.company) && (
            <p className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[13px] text-muted-foreground mt-2">
              {almaMater?.school && (
                <span className="flex items-center gap-1.5">
                  {almaMater.organizationPictureUrl
                    ? <ResizedImage src={almaMater.organizationPictureUrl} renderWidth={16} fallback={false} alt="" className="w-4 h-4 rounded-sm object-cover shrink-0" />
                    : <GraduationCap size={13} className="shrink-0" />}
                  {almaMater.school}
                </span>
              )}
              {almaMater?.school && current?.company && <span aria-hidden>·</span>}
              {current?.company && (
                <span className="flex items-center gap-1.5">
                  {current.organizationPictureUrl
                    ? <ResizedImage src={current.organizationPictureUrl} renderWidth={16} fallback={false} alt="" className="w-4 h-4 rounded-sm object-cover shrink-0" />
                    : <Briefcase size={13} className="shrink-0" />}
                  {current.company}
                </span>
              )}
            </p>
          )}
        </div>

        {person.matchReasons.length > 0 && <MatchReasons reasons={person.matchReasons} />}

        {person.about && <ExpandableAbout text={person.about} />}

        {current && (
          <Section label="Now">
            <ExperienceRow exp={current} />
          </Section>
        )}

        {past.length > 0 && (
          <Section label="Experience">
            <ul className="space-y-3">
              {past.map((e) => (
                <li key={e.id}><ExperienceRow exp={e} /></li>
              ))}
            </ul>
          </Section>
        )}

        {education.length > 0 && (
          <Section label="Education">
            <ul className="space-y-3">
              {education.map((e) => (
                <li key={e.id}><EducationRow ed={e} /></li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}
